/**
 * Endpoints de **Finanzas → Kardex** (`/finanzas/kardex`).
 *
 * Cuatro llamadas: el catálogo de combustibles, el libro del período (que la API
 * calcula y valoriza), la exportación a Excel y el alta de un ajuste manual.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  CuerpoAjuste,
  FiltrosKardex,
  ProductoKardex,
  ReporteKardex,
} from './kardex.types';

export const kardexKeys = {
  todo: ['finanzas', 'kardex'] as const,
  productos: ['finanzas', 'kardex', 'productos'] as const,
  libro: (filtros: FiltrosKardex) => ['finanzas', 'kardex', 'libro', filtros] as const,
};

/** Cadena de consulta con los filtros del kardex. */
export function cadenaDeKardex(filtros: FiltrosKardex): string {
  const parametros = new URLSearchParams({
    fuel_grade_id: String(filtros.fuel_grade_id),
    product_id: String(filtros.product_id),
    start_datetime: filtros.start_datetime,
    end_datetime: filtros.end_datetime,
    valuation_method: filtros.valuation_method,
  });

  return parametros.toString();
}

export const kardexApi = {
  productos: (signal?: AbortSignal) =>
    apiClient.get<{ productos: ProductoKardex[] }>('/api/finanzas/kardex/productos', { signal }),

  libro: (filtros: FiltrosKardex, signal?: AbortSignal) =>
    apiClient.get<ReporteKardex>(`/api/finanzas/kardex?${cadenaDeKardex(filtros)}`, { signal }),

  urlExcel: (filtros: FiltrosKardex) =>
    `/api/finanzas/kardex/exportar?${cadenaDeKardex(filtros)}`,

  guardarAjuste: (cuerpo: CuerpoAjuste) =>
    apiClient.post<{ id: number; message: string }>('/api/finanzas/kardex/ajuste', {
      body: cuerpo,
    }),
};

/** Catálogo de combustibles del kardex. */
export function useProductosKardex() {
  return useQuery({
    queryKey: kardexKeys.productos,
    queryFn: ({ signal }) => kardexApi.productos(signal),
    staleTime: 5 * 60_000,
  });
}

/**
 * El libro del período.
 *
 * Sólo se consulta cuando hay filtros completos (`filtros` es `null` mientras no se
 * pulsa *Consultar*), porque el rango recorre todos los movimientos del producto.
 */
export function useLibroKardex(filtros: FiltrosKardex | null) {
  return useQuery({
    queryKey: [...kardexKeys.todo, 'libro', filtros ?? 'sin-consultar'] as const,
    queryFn: ({ signal }) => kardexApi.libro(filtros as FiltrosKardex, signal),
    enabled: filtros !== null,
  });
}

/** Alta de un ajuste manual de inventario. */
export function useGuardarAjusteKardex() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (cuerpo: CuerpoAjuste) => kardexApi.guardarAjuste(cuerpo),
    onSuccess: () => cliente.invalidateQueries({ queryKey: kardexKeys.todo }),
  });
}
